import { Badge, Button, Space } from 'antd';
import { useRouter } from 'next/router';
import { MouseEventHandler, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useActiveChatEntities from '../../../hooks/plugins/chat/useActiveChatEntities';
import useChatQueueCount from '../../../hooks/plugins/chat/useChatQueueCount';
import { acceptChatFromQueue, resumeChat } from '../../../services/apps/plugins';
import { grispiNotification } from '../../../store/useNotificationStore';
import { nonNullishValue, nullishValue } from '../../../utils/helpers';

interface Props {
    isOnline: boolean;
    isChatQueueTrackable: boolean;
}

const styles = {
    root: { backgroundColor: 'transparent' }
};

const DEFAULT_CONCURRENCY_LIMIT = 5;
// @isOnline prop controls whether to use accepting chat from the queue or not feature
// @isChatQueueTrackable prop controls whether to track chat queue or not
const ChatQueueTracker = ({ isOnline, isChatQueueTrackable }: Props) => {
    const { t } = useTranslation('common');
    const { t: tError } = useTranslation('error');
    const { push } = useRouter();
    const concurrencyLimit = DEFAULT_CONCURRENCY_LIMIT;
    const { data: chatQueueCount, mutate: mutateChatQueueCount } = useChatQueueCount({
        requestFlag: isChatQueueTrackable
    });
    const {
        data: activeChatEntities,
        isLoading,
        mutate
    } = useActiveChatEntities({ revalidateIfStale: false, revalidateOnMount: false });

    const previousChatQueueCount = useRef<number>(null);
    const audioEl = useRef<HTMLAudioElement>(null);
    const [acceptChatStarted, setAcceptChatStarted] = useState(false);

    const hasAtLeastOneItemOnChatQueue = chatQueueCount > 0;
    const hasExceededConcurrencyLimit = isLoading || activeChatEntities?.length >= concurrencyLimit;
    const canAcceptChat = isOnline && !hasExceededConcurrencyLimit && hasAtLeastOneItemOnChatQueue;

    useEffect(() => {
        if (nonNullishValue(chatQueueCount)) {
            if (nullishValue(previousChatQueueCount.current)) {
                previousChatQueueCount.current = chatQueueCount;
                return;
            }

            const shouldRingTheBell = chatQueueCount > previousChatQueueCount.current;

            if (shouldRingTheBell) {
                if (audioEl.current) {
                    audioEl.current.pause();
                    audioEl.current.currentTime = 0; // to start over again
                    audioEl.current.play();
                }
            }
            previousChatQueueCount.current = chatQueueCount;
        }
    }, [chatQueueCount]);

    const handleChatAccept: MouseEventHandler<HTMLElement> = useCallback(async () => {
        if (canAcceptChat) {
            setAcceptChatStarted(true);
            try {
                const acceptedChat = await acceptChatFromQueue();
                const resumedChat = await resumeChat(acceptedChat.chatId);

                await push(`/tickets/${resumedChat.ticketKey}`);
                await mutate();

                setAcceptChatStarted(false);
            } catch (error) {
                if (error?.response?.status === 404) {
                    grispiNotification('warning', tError('noChatAvailable'), 'noChatAvailable');
                    await mutateChatQueueCount();
                    setAcceptChatStarted(false);
                    return;
                }

                grispiNotification('warning', error?.response?.data?.message || error.message, 'errorOnChatAccept');
                setAcceptChatStarted(false);
            }
        } else {
            hasExceededConcurrencyLimit &&
                grispiNotification(
                    'warning',
                    tError('hasExceededConcurrencyLimit', { concurrencyLimit }),
                    'hasExceededConcurrencyLimit'
                );
        }
    }, [canAcceptChat, concurrencyLimit, hasExceededConcurrencyLimit, mutate, mutateChatQueueCount, push, tError]);

    return (
        <>
            <audio ref={audioEl} src="https://bigsoundbank.com/UPLOAD/ogg/2038.ogg" preload="auto" />
            <Button
                type={hasAtLeastOneItemOnChatQueue ? 'primary' : 'default'}
                disabled={!isOnline}
                loading={acceptChatStarted}
                onClick={handleChatAccept}
                style={hasAtLeastOneItemOnChatQueue ? {} : styles.root}
            >
                <Space>
                    {t('plugin.chat.requests')}
                    <Badge
                        count={chatQueueCount}
                        showZero={!chatQueueCount}
                        size="small"
                        overflowCount={10}
                        offset={[0, -2]}
                    />
                </Space>
            </Button>
        </>
    );
};

export default memo(ChatQueueTracker);
